import config from './config.js'

export class ApiError extends Error {
  constructor(message, code = -1, detail = null) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.detail = detail
  }
}

export function isApiError(err) {
  return err instanceof ApiError
}

export function getToken() {
  try {
    return uni.getStorageSync(config.tokenKey) || ''
  } catch (e) {
    return ''
  }
}

export function setToken(token) {
  try {
    if (token) uni.setStorageSync(config.tokenKey, token)
    else uni.removeStorageSync(config.tokenKey)
  } catch (e) {
    /* ignore */
  }
}

// ========== 拦截器容器（axios 风格） ==========
const _requestInterceptors = []
const _responseInterceptors = []
const _responseErrorInterceptors = []

const interceptors = {
  request: {
    use(fn) {
      _requestInterceptors.push(fn)
      return () => {
        const i = _requestInterceptors.indexOf(fn)
        if (i >= 0) _requestInterceptors.splice(i, 1)
      }
    }
  },
  response: {
    // 成功响应器：返回原始 body（{code,message,data}）；可链式串改
    use(fn) {
      _responseInterceptors.push(fn)
      return () => {
        const i = _responseInterceptors.indexOf(fn)
        if (i >= 0) _responseInterceptors.splice(i, 1)
      }
    },
    // 错误响应器：捕获 ApiError/网络错误，可统一 toast / 重定向登录等
    useError(fn) {
      _responseErrorInterceptors.push(fn)
      return () => {
        const i = _responseErrorInterceptors.indexOf(fn)
        if (i >= 0) _responseErrorInterceptors.splice(i, 1)
      }
    }
  }
}

async function runRequestInterceptors(options) {
  let cur = options
  for (const fn of _requestInterceptors) {
    const ret = await fn(cur)
    if (ret && typeof ret === 'object') cur = ret
  }
  return cur
}

async function runResponseInterceptors(body) {
  let cur = body
  for (const fn of _responseInterceptors) {
    const ret = await fn(cur)
    if (ret !== undefined && ret !== null) cur = ret
  }
  return cur
}

async function runResponseErrorInterceptors(err) {
  for (const fn of _responseErrorInterceptors) {
    try {
      await fn(err)
    } catch (e) {
      /* 忽略错误响应器自身异常 */
    }
  }
}

// ========== URL / Header 工具 ==========
function buildUrl(url) {
  if (/^https?:\/\//.test(url)) return url
  const withPrefix = config.prefix && !url.startsWith(config.prefix)
    ? config.prefix + (url.startsWith('/') ? url : '/' + url)
    : url
  return config.baseURL ? config.baseURL + (withPrefix.startsWith('/') ? withPrefix : '/' + withPrefix) : withPrefix
}

function toQuery(params) {
  if (!params) return ''
  const parts = Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== '')
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
  return parts.length ? '?' + parts.join('&') : ''
}

function readDefaultHeader(extra = {}) {
  const header = { ...(extra.header || {}) }
  if (extra.token !== false && getToken()) {
    header.Authorization = 'Bearer ' + getToken()
  }
  return header
}

// ========== 业务响应解析 ==========
// body: { code, message, data }；code===successCode 成功；解析失败抛 ApiError
function parseBody(body, { url, method }) {
  if (body && typeof body === 'object' && body.code !== undefined) {
    if (body.code !== config.successCode) {
      const msg = (body && (body.message || body.msg)) || '业务处理失败'
      throw new ApiError(msg, body.code, { url, method, body })
    }
    return body.data
  }
  return body
}

function normalizeNetworkError(err, url, method) {
  const rawMsg = (err && (err.errMsg || err.message)) || '网络异常，请稍后重试'
  const networkMarkers = ['timeout', 'abort', 'request:fail', 'no permission']
  const isNetwork = networkMarkers.some((k) => String(rawMsg).toLowerCase().includes(k.toLowerCase()))
  const e = new ApiError(isNetwork ? '网络异常，请稍后重试' : rawMsg, -1, { url, method, raw: err })
  runResponseErrorInterceptors(e)
  return e
}

// ========== 核心 ==========
function request(options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const url = buildUrl(options.url)
  const header = readDefaultHeader(options)
  const opts = { ...options, method, url, header, timeout: options.timeout || config.timeout }

  return new Promise(async (resolve, reject) => {
    let finalOpts
    try {
      finalOpts = await runRequestInterceptors(opts)
    } catch (e) {
      reject(e instanceof ApiError ? e : new ApiError(e && e.message ? e.message : '请求拦截异常', -1, e))
      return
    }

    uni.request({
      url: finalOpts.url,
      method: finalOpts.method,
      data: finalOpts.data,
      header: finalOpts.header,
      timeout: finalOpts.timeout,
      success: async (res) => {
        const status = res.statusCode
        try {
          let body = res.data
          if (status && (status < 200 || status >= 300)) {
            const msg = (body && (body.message || body.msg)) || `请求失败 (HTTP ${status})`
            throw new ApiError(msg, status, { url, method, status, body })
          }
          body = await runResponseInterceptors(body)
          resolve(parseBody(body, { url, method }))
        } catch (e) {
          if (!(e instanceof ApiError)) e = new ApiError(e && e.message ? e.message : '请求解析失败', -1, { url, method })
          await runResponseErrorInterceptors(e)
          reject(e)
        }
      },
      fail: (err) => {
        const e = normalizeNetworkError(err, url, method)
        reject(e)
      }
    })
  })
}

export function get(url, params, options = {}) {
  return request({ method: 'GET', url: url + toQuery(params), ...options })
}

export function post(url, data, options = {}) {
  return request({ method: 'POST', url, data, ...options })
}

export function put(url, data, options = {}) {
  return request({ method: 'PUT', url, data, ...options })
}

export function del(url, params, options = {}) {
  return request({ method: 'DELETE', url: url + toQuery(params), ...options })
}

export function upload(url, { filePath, name = 'image', formData = {}, header, timeout } = {}) {
  const full = buildUrl(url)

  return new Promise((resolve, reject) => {
    uni.uploadFile({
      url: full,
      filePath,
      name,
      formData,
      header: readDefaultHeader({ header }),
      timeout: timeout || config.uploadTimeout,
      success: async (res) => {
        const status = res.statusCode
        try {
          let body = res.data
          if (typeof body === 'string' && body) {
            try {
              body = JSON.parse(body)
            } catch (e) {
              /* 保持原字符串 */
            }
          }
          if (status && (status < 200 || status >= 300)) {
            const msg = (body && (body.message || body.msg)) || `请求失败 (HTTP ${status})`
            throw new ApiError(msg, status, { url: full, method: 'UPLOAD', status, body })
          }
          body = await runResponseInterceptors(body)
          resolve(parseBody(body, { url: full, method: 'UPLOAD' }))
        } catch (e) {
          if (!(e instanceof ApiError)) e = new ApiError(e && e.message ? e.message : '上传解析失败', -1, { url: full })
          await runResponseErrorInterceptors(e)
          reject(e)
        }
      },
      fail: (err) => {
        const e = normalizeNetworkError(err, full, 'UPLOAD')
        reject(e)
      }
    })
  })
}

const service = { request, get, post, put, del, upload, interceptors }

export { interceptors }

export default service
